import {
  ClothingProcurementDepartmentSizeStatistics,
  ClothingProcurementForecast,
  ClothingProcurementForecastRow,
  ClothingProcurementSizeCount
} from '../../../models/clothing.model';
import { CLOTHING_PROCUREMENT_DEFAULTS } from './clothing.constants';
import {
  ClothingProcurementDepartmentMappingContext,
  ClothingProcurementDepartmentSizeView,
  ClothingProcurementDepartmentSizeViewIndex,
  ClothingProcurementDepartmentView,
  ClothingProcurementForecastViewRow,
  ClothingProcurementMappingContext,
  ClothingProcurementSizeBar
} from './clothing-procurement-forecast.option';

export class ClothingProcurementForecastMapper {
  public map(forecast: ClothingProcurementForecast): ClothingProcurementDepartmentView[] {
    return this.groupByDepartment(this.mapRows(forecast));
  }

  private mapRows(forecast: ClothingProcurementForecast): ClothingProcurementForecastViewRow[] {
    const context: ClothingProcurementMappingContext = {
      rows: [],
      rowIndex: Object.create(null),
      statistics: this.indexSizeStatistics(forecast.departmentSizeStatistics || [])
    };
    forecast.rows.filter((row) => row.totalRequired > CLOTHING_PROCUREMENT_DEFAULTS.emptyCount)
      .forEach((row) => this.mergeForecastRow(context, row));
    return context.rows;
  }

  private groupByDepartment(
    rows: ClothingProcurementForecastViewRow[]
  ): ClothingProcurementDepartmentView[] {
    const context: ClothingProcurementDepartmentMappingContext = {
      departments: [],
      departmentIndex: Object.create(null)
    };
    rows.forEach((row) => this.addDepartmentRow(context, row));
    return context.departments;
  }

  private addDepartmentRow(
    context: ClothingProcurementDepartmentMappingContext,
    row: ClothingProcurementForecastViewRow
  ) {
    const department = context.departmentIndex[row.groupId] || this.createDepartment(row);
    if (!context.departmentIndex[row.groupId]) {
      context.departmentIndex[row.groupId] = department;
      context.departments.push(department);
    }
    department.rows.push(row);
    department.totalRequired += row.totalRequired;
  }

  private createDepartment(
    row: ClothingProcurementForecastViewRow
  ): ClothingProcurementDepartmentView {
    return {
      groupId: row.groupId,
      groupName: row.groupName,
      panelId: CLOTHING_PROCUREMENT_DEFAULTS.departmentPanelPrefix + row.groupId,
      rows: [],
      sizeStatistics: row.sizeStatistics,
      totalRequired: CLOTHING_PROCUREMENT_DEFAULTS.emptyCount
    };
  }

  private mergeForecastRow(
    context: ClothingProcurementMappingContext,
    source: ClothingProcurementForecastRow
  ) {
    const key = this.createViewRowKey(source);
    const existing = context.rowIndex[key];
    if (existing) {
      this.addForecastQuantities(existing, source);
      return;
    }
    const row = this.createForecastRow(key, source, context.statistics[source.groupId]);
    context.rowIndex[key] = row;
    context.rows.push(row);
  }

  private createViewRowKey(row: ClothingProcurementForecastRow): string {
    return row.groupId + CLOTHING_PROCUREMENT_DEFAULTS.rowKeySeparator + row.catalogItemId;
  }

  private createForecastRow(
    key: string,
    source: ClothingProcurementForecastRow,
    statistics: ClothingProcurementDepartmentSizeView
  ): ClothingProcurementForecastViewRow {
    return {
      key: key,
      groupId: source.groupId,
      groupName: source.groupName,
      catalogItemId: source.catalogItemId,
      catalogItemName: source.catalogItemName,
      currentActive: source.currentActive,
      missingNow: source.missingNow,
      expiringByTarget: source.expiringByTarget,
      totalRequired: source.totalRequired,
      sizeStatistics: statistics || this.createEmptySizeStatistics(source.groupId)
    };
  }

  private addForecastQuantities(
    target: ClothingProcurementForecastViewRow,
    source: ClothingProcurementForecastRow
  ) {
    target.currentActive += source.currentActive;
    target.missingNow += source.missingNow;
    target.expiringByTarget += source.expiringByTarget;
    target.totalRequired += source.totalRequired;
  }

  private indexSizeStatistics(
    statistics: ClothingProcurementDepartmentSizeStatistics[]
  ): ClothingProcurementDepartmentSizeViewIndex {
    const index: ClothingProcurementDepartmentSizeViewIndex = Object.create(null);
    statistics.forEach((item) => index[item.groupId] = this.createSizeStatistics(item));
    return index;
  }

  private createSizeStatistics(
    statistics: ClothingProcurementDepartmentSizeStatistics
  ): ClothingProcurementDepartmentSizeView {
    const clothingSizes = this.createSizeBars(statistics.clothingSizes);
    const shoeSizes = this.createSizeBars(statistics.shoeSizes);
    return {
      groupId: statistics.groupId,
      clothingSizes: clothingSizes,
      shoeSizes: shoeSizes,
      hasSizes: clothingSizes.length > CLOTHING_PROCUREMENT_DEFAULTS.emptyCount
        || shoeSizes.length > CLOTHING_PROCUREMENT_DEFAULTS.emptyCount
    };
  }

  private createSizeBars(sizes: ClothingProcurementSizeCount[]): ClothingProcurementSizeBar[] {
    const maximum = sizes.reduce(
      (result, size) => Math.max(result, size.employeeCount),
      CLOTHING_PROCUREMENT_DEFAULTS.emptyCount
    );
    return sizes.map((size) => ({
      size: size.size,
      employeeCount: size.employeeCount,
      percentage: this.calculateBarPercentage(size.employeeCount, maximum)
    }));
  }

  private calculateBarPercentage(employeeCount: number, maximum: number): number {
    if (!maximum) {
      return CLOTHING_PROCUREMENT_DEFAULTS.emptyCount;
    }
    return Math.round(employeeCount / maximum * CLOTHING_PROCUREMENT_DEFAULTS.fullPercentage);
  }

  private createEmptySizeStatistics(groupId: string): ClothingProcurementDepartmentSizeView {
    return {
      groupId: groupId,
      clothingSizes: [],
      shoeSizes: [],
      hasSizes: false
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/clothing/clothing-procurement-forecast.mapper.ts